import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useReadContracts } from 'wagmi';
import { formatUnits, type Address } from 'viem';
import { polygon, mainnet } from 'wagmi/chains';
import { AlertTriangle, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../../../tokens';

// FAR Token addresses per chain (Production Mainnet)
const FAR_TOKEN_ADDRESSES: Record<number, Address> = {
  [polygon.id]: '0x5F32AbeeBD3c2fac1E7459A27e1AE9f1C16ccccA',
  [mainnet.id]: '0xf9Fa60EF4F23F00CCE403cc4d2c11Baf4880A0d6',
};

// Minimal ERC20 ABI for balanceOf
const TOKEN_ABI = [
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

interface BridgeBalanceStatusProps {
  address: Address | undefined;
}

/**
 * BridgeBalanceStatus - Shows FAR token balances on ETH/Polygon with bridge reminders
 *
 * - Orange warning if there are tokens on Ethereum or Polygon
 * - Green success if all tokens are bridged (no balance on ETH/Polygon)
 */
export const BridgeBalanceStatus: React.FC<BridgeBalanceStatusProps> = ({ address }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = React.useState<string | null>(null);

  // Fetch balances from Polygon and Ethereum
  const { data, isLoading } = useReadContracts({
    contracts: [
      {
        address: FAR_TOKEN_ADDRESSES[polygon.id],
        abi: TOKEN_ABI,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
        chainId: polygon.id,
      },
      {
        address: FAR_TOKEN_ADDRESSES[mainnet.id],
        abi: TOKEN_ABI,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
        chainId: mainnet.id,
      },
    ],
    query: {
      enabled: !!address,
    },
  });

  const polygonBalance = data?.[0]?.result as bigint | undefined;
  const ethereumBalance = data?.[1]?.result as bigint | undefined;

  const hasPolygonBalance = polygonBalance && polygonBalance > 0n;
  const hasEthereumBalance = ethereumBalance && ethereumBalance > 0n;
  const allBridged = !hasPolygonBalance && !hasEthereumBalance;

  const formatBalance = (balance: bigint | undefined): string => {
    if (!balance) return '0';
    return parseFloat(formatUnits(balance, 18)).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (isLoading) {
    return (
      <div
        style={{
          padding: spacing.lg,
          backgroundColor: colors.background.secondary.default,
          borderRadius: borders.component.card.radius,
          border: `1px solid ${colors.border.default.default}`,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
        }}
      >
        <Loader2 size={20} color={colors.text.muted.default} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ color: colors.text.muted.default, fontSize: typography.fontSize.sm }}>
          Checking balances on other networks...
        </span>
      </div>
    );
  }

  // Show success message if all bridged
  if (allBridged) {
    return (
      <div>
        <style>{`
          @media (max-width: 500px) {
            .bridge-balance-success {
              flex-direction: column !important;
              align-items: flex-start !important;
              text-align: left !important;
            }
          }
        `}</style>
        <div
          className="bridge-balance-success"
          style={{
            padding: spacing.lg,
            backgroundColor: `${colors.semantic.success}15`,
            borderRadius: borders.component.card.radius,
            border: `1px solid ${colors.semantic.success}40`,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
        <CheckCircle size={24} color={colors.semantic.success} />
        <div>
          <div
            style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.semantic.success,
              marginBottom: spacing.xs,
            }}
          >
            All FAR tokens bridged!
          </div>
          <div
            style={{
              fontSize: typography.fontSize.xs,
              color: colors.text.secondary.default,
            }}
          >
            No FAR tokens found on Ethereum or Polygon networks.
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Show warnings for each network with balance
  return (
    <div>
      <style>{`
        @media (max-width: 500px) {
          .bridge-balance-warning {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .bridge-balance-warning-button {
            width: 100% !important;
            justify-content: center !important;
          }
        }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {hasEthereumBalance ? (
        <div
          style={{
            padding: spacing.lg,
            backgroundColor: `${colors.semantic.warning}15`,
            borderRadius: borders.component.card.radius,
            border: `1px solid ${colors.semantic.warning}40`,
          }}
        >
          <div className="bridge-balance-warning" style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
            <AlertTriangle size={24} color={colors.semantic.warning} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.semantic.warning,
                  marginBottom: spacing.xs,
                }}
              >
                FAR tokens on Ethereum
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text.primary.default,
                  marginBottom: spacing.sm,
                }}
              >
                {formatBalance(ethereumBalance)} FAR
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.secondary.default,
                  marginBottom: spacing.md,
                }}
              >
                Bridge these tokens to BSC to swap them for nFAR.
              </div>
              <button
                className="bridge-balance-warning-button"
                onClick={() => navigate('/bridge')}
                onMouseEnter={() => setIsHovered('eth')}
                onMouseLeave={() => setIsHovered(null)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  padding: `${spacing.sm} ${spacing.md}`,
                  backgroundColor: isHovered === 'eth' ? colors.semantic.warning : `${colors.semantic.warning}20`,
                  color: isHovered === 'eth' ? colors.background.primary.default : colors.semantic.warning,
                  border: `1px solid ${colors.semantic.warning}`,
                  borderRadius: borders.component.button.radius,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Bridge from Ethereum
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {hasPolygonBalance ? (
        <div
          style={{
            padding: spacing.lg,
            backgroundColor: `${colors.semantic.warning}15`,
            borderRadius: borders.component.card.radius,
            border: `1px solid ${colors.semantic.warning}40`,
          }}
        >
          <div className="bridge-balance-warning" style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
            <AlertTriangle size={24} color={colors.semantic.warning} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.semantic.warning,
                  marginBottom: spacing.xs,
                }}
              >
                FAR tokens on Polygon
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text.primary.default,
                  marginBottom: spacing.sm,
                }}
              >
                {formatBalance(polygonBalance)} FAR
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.secondary.default,
                  marginBottom: spacing.md,
                }}
              >
                Bridge these tokens to BSC to swap them for nFAR.
              </div>
              <button
                className="bridge-balance-warning-button"
                onClick={() => navigate('/bridge')}
                onMouseEnter={() => setIsHovered('pol')}
                onMouseLeave={() => setIsHovered(null)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  padding: `${spacing.sm} ${spacing.md}`,
                  backgroundColor: isHovered === 'pol' ? colors.semantic.warning : `${colors.semantic.warning}20`,
                  color: isHovered === 'pol' ? colors.background.primary.default : colors.semantic.warning,
                  border: `1px solid ${colors.semantic.warning}`,
                  borderRadius: borders.component.button.radius,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Bridge from Polygon
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
};

export default BridgeBalanceStatus;
